// ============================================================================
// chatImageAttachment — одиночная картинка и плитка сетки в сообщении.
//
// Пропорции: изображение НЕ растягивается и НЕ обрезается (object-contain,
// max-width/max-height); пузырь подстраивается под картинку. Превью — серверный
// thumb (быстро, дёшево); до загрузки — иконка-плейсхолдер (не пустой блок),
// после ошибки — плейсхолдер с именем/размером, клик всё равно открывает
// полный просмотр (FilePreviewModal). Сетка — равные плитки (object-cover).
// ============================================================================

import { useEffect, useState } from 'react';
import { ImageIcon, ImageOff } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { fileBase64 } from '@/lib/fileBytes';
import { logSwallowed } from '@/lib/errors';
import { formatBytes } from '@/components/chat/chatInfoData';
import {
  MESSAGE_MEDIA_INNER_MAX_WIDTH_PX,
  MESSAGE_SINGLE_MEDIA_MAX_HEIGHT,
  MESSAGE_SINGLE_MEDIA_MAX_HEIGHT_PX,
} from '@/lib/chatMessageLayout';

export interface ChatImageRef {
  id: string;
  name: string;
  size: number;
}

type ThumbState =
  | { status: 'loading' }
  | { status: 'ready'; url: string; width: number; height: number }
  | { status: 'failed' };

/** Размер картинки из превью — ДО отрисовки (рамка не «прыгает»). */
function decodeImageSize(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('не удалось прочитать изображение'));
    img.src = url;
  });
}

/** Серверная миниатюра файла (один запрос на id/ширину). */
function useFileThumb(id: string, width: number): ThumbState {
  const [state, setState] = useState<ThumbState>({ status: 'loading' });
  useEffect(() => {
    let alive = true;
    setState({ status: 'loading' });
    getSDK().files.getFileThumb({ id, width })
      .then(async (r) => {
        if (!alive) return;
        const b64 = fileBase64(r.data);
        if (!b64) {
          if (alive) setState({ status: 'failed' });
          return;
        }
        const url = `data:image/jpeg;base64,${b64}`;
        try {
          const size = await decodeImageSize(url);
          if (alive) setState({ status: 'ready', url, ...size });
        } catch (e: unknown) {
          logSwallowed(e, 'components/chat/chatImageAttachment.tsx:decode');
          if (alive) setState({ status: 'failed' });
        }
      })
      .catch((e: unknown) => {
        logSwallowed(e, 'components/chat/chatImageAttachment.tsx:thumb');
        if (alive) setState({ status: 'failed' });
      });
    return () => { alive = false; };
  }, [id, width]);
  return state;
}

/** Вписывание натуральных пропорций в медиа-полосу пузыря. */
function fitImageSize(width: number, height: number): { width: number; height: number } {
  const safeW = width > 0 ? width : 1;
  const safeH = height > 0 ? height : 1;
  const scale = Math.min(
    1,
    MESSAGE_MEDIA_INNER_MAX_WIDTH_PX / safeW,
    MESSAGE_SINGLE_MEDIA_MAX_HEIGHT_PX / safeH,
  );
  return { width: Math.max(1, Math.round(safeW * scale)), height: Math.max(1, Math.round(safeH * scale)) };
}

function imageMeta(image: ChatImageRef): string {
  return [formatBytes(image.size), 'изображение'].filter(Boolean).join(' · ');
}

/** Плейсхолдер без превью: иконка + имя/размер (вложение не «пустое»). */
function ImageFallback({ image }: { image: ChatImageRef }) {
  return (
    <span className="flex max-w-full items-center gap-2 rounded-xl border px-2.5 py-2">
      <ImageOff className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[11px] font-medium" title={image.name}>{image.name}</span>
        <span className="block text-[10px] text-muted-foreground">{imageMeta(image)}</span>
      </span>
    </span>
  );
}

function ImageSkeleton({ tile }: { tile?: boolean }) {
  if (tile) {
    return (
      <span className="absolute inset-0 flex items-center justify-center bg-muted/50 animate-pulse">
        <ImageIcon className="h-4 w-4 text-muted-foreground/60" />
      </span>
    );
  }
  return (
    <span
      className="flex aspect-[4/3] w-[220px] max-w-full items-center justify-center rounded-xl border bg-muted/50 animate-pulse"
      aria-label="Загрузка изображения"
    >
      <ImageIcon className="h-6 w-6 text-muted-foreground/60" />
    </span>
  );
}

/** Одиночное изображение: пропорциональная рамка + существующий просмотр. */
export function ChatImageAttachment({ image, onOpen }: {
  image: ChatImageRef;
  onOpen: (fileId: string, fileName: string) => void;
}) {
  const thumb = useFileThumb(image.id, 720);
  if (thumb.status === 'loading') return <ImageSkeleton />;
  if (thumb.status === 'failed') {
    return (
      <button
        type="button"
        className="block max-w-full text-left"
        onClick={() => onOpen(image.id, image.name)}
        title={`${image.name} · открыть`}
      >
        <ImageFallback image={image} />
      </button>
    );
  }
  const size = thumb.status === 'ready' ? fitImageSize(thumb.width, thumb.height) : null;
  return (
    <span className="block max-w-full min-w-0">
      <button
        type="button"
        className="block max-w-full cursor-zoom-in overflow-hidden rounded-t-xl border border-b-0 shadow-sm bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
        onClick={() => onOpen(image.id, image.name)}
        title={`${image.name}${image.size > 0 ? ` · ${formatBytes(image.size)}` : ''} · открыть`}
      >
        {thumb.status === 'ready' && size && (
          <img
            src={thumb.url}
            alt={image.name}
            width={size.width}
            height={size.height}
            draggable={false}
            className="block h-auto max-w-full object-contain"
            style={{ maxHeight: MESSAGE_SINGLE_MEDIA_MAX_HEIGHT }}
          />
        )}
      </button>
      {/* Подпись под картинкой: имя файла видно всегда (не только в title). */}
      <span className="flex max-w-full items-center gap-1.5 rounded-b-xl border bg-muted/30 px-2 py-1">
        <ImageIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-[10px] text-muted-foreground" title={image.name}>
          {image.name}
        </span>
        {image.size > 0 && (
          <span className="shrink-0 text-[10px] text-muted-foreground/70">{formatBytes(image.size)}</span>
        )}
      </span>
    </span>
  );
}

/** Плитка сетки: равные квадраты, обрезка object-cover (допустима в сетке). */
export function ChatImageTile({ image, onOpen }: {
  image: ChatImageRef;
  onOpen: (fileId: string, fileName: string) => void;
}) {
  const thumb = useFileThumb(image.id, 480);
  return (
    <button
      type="button"
      className="relative aspect-square w-full min-w-0 overflow-hidden rounded-lg border bg-muted/40"
      onClick={() => onOpen(image.id, image.name)}
      title={`${image.name}${image.size > 0 ? ` · ${formatBytes(image.size)}` : ''}`}
    >
      {thumb.status === 'ready' ? (
        <img
          src={thumb.url}
          alt={image.name}
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : thumb.status === 'failed' ? (
        <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-1 text-center">
          <ImageOff className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="w-full truncate text-[9px] text-muted-foreground" title={image.name}>{image.name}</span>
        </span>
      ) : (
        <ImageSkeleton tile />
      )}
    </button>
  );
}
